import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, X, ExternalLink, RefreshCw, Pencil, Check, ChevronLeft } from "lucide-react";
import api, { getUser } from "@/lib/api";


// Small helper — human-friendly delta ("há 5 min", "ontem", …) used
// on each Zoho note timestamp.
const relativeTime = (iso) => {
  if (!iso) return "";
  const then = new Date(iso);
  if (isNaN(then.getTime())) return "";
  const diff = Math.floor((Date.now() - then.getTime()) / 1000);
  if (diff < 60) return "agora mesmo";
  if (diff < 3600) return `há ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `há ${Math.floor(diff / 3600)} h`;
  if (diff < 172800) return "ontem";
  if (diff < 2592000) return `há ${Math.floor(diff / 86400)} dias`;
  return then.toLocaleDateString("pt-PT");
};

// Was this note created today in Europe/Lisbon? (mirror of the backend rule)
const isSameDay = (iso) => {
  if (!iso) return false;
  try {
    const then = new Date(iso);
    const now = new Date();
    // Compare year/month/day in the LOCAL timezone (browser). Close-enough
    // proxy for Europe/Lisbon (the vast majority of users are LX-based).
    return then.getFullYear() === now.getFullYear()
        && then.getMonth() === now.getMonth()
        && then.getDate() === now.getDate();
  } catch { return false; }
};


// ---------------------------------------------------------------------------
// ZohoNotesBlock — Now rendered as a FIXED right-edge vertical drawer so it
// doesn't fight for space with the LeadDetail center column. Collapsed state
// shows a slim vertical tab with the notes count; expanded state slides a
// 380px panel from the right edge.
//
// Also supports inline edit for notes the current user OWNS and that were
// created TODAY (mirrors the backend PATCH /leads/{id}/zoho-notes/{note_id}
// guardrails). Admins can edit any note.
// ---------------------------------------------------------------------------
export default function ZohoNotesBlock({ lead }) {
  const me = getUser();
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [notes, setNotes] = useState([]);
  const [message, setMessage] = useState("");
  const [ok, setOk] = useState(true);
  const [expanded, setExpanded] = useState(false);
  // Inline edit state
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState("");
  const [savingId, setSavingId] = useState(null);
  const hasZoho = !!lead?.zoho_lead_id;

  const load = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const r = await api.get(`/leads/${lead.id}/zoho-notes`);
      const data = r.data || {};
      setNotes(Array.isArray(data.notes) ? data.notes : []);
      setMessage(data.message || "");
      setOk(data.ok !== false);
    } catch (e) {
      setOk(false);
      setMessage(e.response?.data?.detail || e.message || "Erro a carregar notas");
      setNotes([]);
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  };

  useEffect(() => {
    if (hasZoho && !loaded && !loading) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasZoho, lead?.id]);

  const canEdit = (n) => {
    if (!n?.id || !n?.created_at) return false;
    if (me?.role === "admin") return true;
    const myEmail = (me?.email || "").toLowerCase();
    const owner = (n.owner_email || "").toLowerCase();
    return owner && owner === myEmail && isSameDay(n.created_at);
  };

  const startEdit = (n) => {
    setEditingId(n.id);
    setEditDraft(n.content || "");
  };
  const cancelEdit = () => { setEditingId(null); setEditDraft(""); };
  const saveEdit = async (n) => {
    const trimmed = (editDraft || "").trim();
    if (!trimmed) { toast.error("Conteúdo não pode ficar vazio"); return; }
    setSavingId(n.id);
    try {
      await api.patch(`/leads/${lead.id}/zoho-notes/${n.id}`, { content: trimmed });
      toast.success("Nota actualizada no Zoho");
      // Reload notes to reflect Zoho state
      await load();
      cancelEdit();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao actualizar nota");
    } finally { setSavingId(null); }
  };

  return (
    <>
      {/* Collapsed tab glued to the right edge — always visible */}
      {!expanded && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          data-testid="zoho-notes-toggle"
          className="fixed top-[calc(50%-45px)] right-0 z-40 flex flex-col items-center gap-2 py-4 px-2 rounded-l-2xl bg-card border border-r-0 border-border shadow-[0_10px_40px_-10px_rgba(15,27,44,0.14)] hover:bg-muted transition-colors group"
          title="Ver notas Zoho"
          aria-label="Abrir notas do Zoho CRM"
        >
          <FileText className="w-4 h-4 text-primary" />
          {loaded && ok && (
            <span data-testid="zoho-notes-count" className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full bg-primary text-primary-foreground text-[11px] font-bold">
              {notes.length}
            </span>
          )}
          <span className="[writing-mode:vertical-rl] rotate-180 text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground group-hover:text-foreground">
            Zoho
          </span>
        </button>
      )}

      {/* Expanded drawer — slides from the right */}
      {expanded && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40 bg-foreground/20 backdrop-blur-[2px]"
            onClick={() => setExpanded(false)}
            data-testid="zoho-notes-backdrop"
          />
          {/* Panel */}
          <div
            id="zoho-notes"
            data-testid="zoho-notes-section"
            className="fixed top-0 right-0 z-50 h-full w-[380px] max-w-[92vw] bg-card border-l border-border shadow-[0_20px_60px_-20px_rgba(15,27,44,0.5)] flex flex-col animate-in slide-in-from-right duration-200"
          >
            {/* Header */}
            <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-border">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div className="w-9 h-9 rounded-full bg-muted border border-border flex items-center justify-center flex-shrink-0">
                  <FileText className="w-4 h-4 text-primary" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="font-ui text-sm font-semibold text-foreground truncate">
                    Notas do Zoho CRM
                  </h3>
                  <div className="text-[11px] text-muted-foreground truncate">
                    {loading ? "A carregar…" : (loaded && ok ? `${notes.length} nota${notes.length === 1 ? "" : "s"}` : "Histórico do lead")}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={load}
                  disabled={loading || !hasZoho}
                  data-testid="btn-refresh-zoho-notes"
                  className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed"
                  title="Actualizar"
                >
                  <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                </button>
                {hasZoho && (
                  <a
                    href={`https://crm.zoho.com/crm/tab/Leads/${lead.zoho_lead_id}`}
                    target="_blank" rel="noopener noreferrer"
                    data-testid="btn-open-zoho"
                    className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted"
                    title="Abrir no Zoho CRM"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                )}
                <button
                  onClick={() => setExpanded(false)}
                  data-testid="zoho-notes-close"
                  className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted"
                  title="Fechar"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
              {!hasZoho && (
                <div className="text-sm text-muted-foreground bg-muted/40 border border-dashed border-border rounded-lg p-4">
                  Este lead ainda não tem contrapartida no <b>Zoho CRM</b>. As notas ficam disponíveis assim que o lead for sincronizado.
                </div>
              )}
              {hasZoho && loading && (
                <div className="text-sm text-muted-foreground italic">A carregar notas…</div>
              )}
              {hasZoho && loaded && !ok && (
                <div className="text-sm text-[hsl(var(--danger))] bg-[hsl(var(--danger-bg))] border border-[hsl(var(--danger))]/25 rounded-lg p-4" data-testid="zoho-notes-error">
                  {message || "Não foi possível obter as notas do Zoho."}
                </div>
              )}
              {hasZoho && loaded && ok && notes.length === 0 && (
                <div className="text-sm text-muted-foreground italic bg-muted/40 border border-dashed border-border rounded-lg p-4">
                  Nenhuma nota registada no Zoho CRM.
                </div>
              )}
              {hasZoho && loaded && ok && notes.map((n) => {
                const editable = canEdit(n);
                const isEditing = editingId === n.id;
                return (
                  <div key={n.id} data-testid="zoho-note"
                        className="border border-border rounded-xl p-3.5 bg-muted/30 hover:bg-muted/50 transition-colors">
                    <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                      <div className="flex items-center gap-1.5 flex-wrap min-w-0">
                        <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.15em] font-bold text-primary bg-primary/15 border border-primary/30 rounded-full px-2 py-0.5">
                          Zoho
                        </span>
                        {n.owner_name && (
                          <span className="text-[11px] text-muted-foreground truncate">
                            <b className="text-foreground">{n.owner_name}</b>
                          </span>
                        )}
                      </div>
                      {n.created_at && (
                        <span className="text-[10px] text-muted-foreground" title={new Date(n.created_at).toLocaleString("pt-PT")}>
                          {relativeTime(n.created_at)}
                        </span>
                      )}
                    </div>
                    {n.title && (
                      <div className="text-[13px] font-semibold text-foreground mb-1.5" data-testid="zoho-note-title">{n.title}</div>
                    )}
                    {isEditing ? (
                      <div className="space-y-2">
                        <textarea
                          value={editDraft}
                          onChange={(e) => setEditDraft(e.target.value)}
                          rows={5}
                          className="w-full text-[13px] text-foreground bg-card border border-primary rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/30"
                          data-testid={`zoho-note-edit-textarea-${n.id}`}
                          autoFocus
                        />
                        <div className="flex items-center gap-2 justify-end">
                          <button
                            onClick={cancelEdit}
                            className="px-3 py-1.5 rounded-lg text-[11px] font-medium text-muted-foreground hover:bg-muted"
                            data-testid={`zoho-note-edit-cancel-${n.id}`}
                          >Cancelar</button>
                          <button
                            onClick={() => saveEdit(n)}
                            disabled={savingId === n.id}
                            data-testid={`zoho-note-edit-save-${n.id}`}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-[11px] font-semibold bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
                          >
                            {savingId === n.id ? "A gravar…" : <><Check className="w-3 h-3" /> Gravar</>}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="relative group">
                        <div className="text-[12.5px] text-foreground/85 whitespace-pre-wrap leading-relaxed break-words" data-testid="zoho-note-content">
                          {(n.content || "").slice(0, 4000)}
                          {n.content && n.content.length > 4000 && <span className="text-muted-foreground"> …</span>}
                        </div>
                        {editable && (
                          <button
                            type="button"
                            onClick={() => startEdit(n)}
                            data-testid={`zoho-note-edit-${n.id}`}
                            className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-primary bg-card border border-primary/40 rounded-full px-2 py-0.5 hover:bg-primary hover:text-primary-foreground"
                            title="Editar (só hoje)"
                          >
                            <Pencil className="w-3 h-3" /> Editar
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </>
  );
}
